import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Avatar,
  Upload,
  Button,
  Descriptions,
  Progress,
  Modal,
  message,
  Form,
  Input,
  Spin,
  Tooltip,
  Badge,
  Alert,
} from "antd";
import {
  UserOutlined,
  UploadOutlined,
  EditOutlined,
  CrownOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  RocketOutlined,
  PhoneOutlined,
  MailOutlined,
  HomeOutlined,
  BankOutlined,
  IdcardOutlined,
  SafetyOutlined,
  DollarOutlined,
  FileTextOutlined,
  FolderOutlined,
  CloudOutlined,
  TeamOutlined,
  CameraOutlined,
} from "@ant-design/icons";
import { profileService } from "../services/api.service";
import axiosInstance from "../utils/axios.config";
import logger from "../utils/logger";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { motion } from "framer-motion";
import moment from "moment";
import "moment/locale/ar";

moment.locale("ar");

const Profile = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [tenantData, setTenantData] = useState(null); // ✅ بيانات المكتب
  const [profile, setProfile] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    officePhone: "",
    officeName: "",
    officeAddress: "",
    licenseNumber: "",
    taxNumber: "",
    commercialRegister: "",
    bankAccount: "",
    bankName: "",
    logo: null,
    subscription: {
      plan: "مجاني",
      expiryDate: "غير محدد",
      casesLimit: 10,
      casesUsed: 0,
      documentsLimit: 50,
      documentsUsed: 0,
      storageLimit: 100,
      storageUsed: 0,
    },
  });
  const navigate = useNavigate();

  useEffect(() => {
    // نقوم بتحميل البروفايل أولاً، لأنه يقوم بتحديث بيانات المستأجر في التخزين المحلي
    // مما يضمن أن الطلبات اللاحقة تحتوي على الهيدر الصحيح
    loadProfile().then(() => {
      loadSubscriptionStatus();
      loadTenantData();
    });
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const response = await profileService.getProfile();

      logger.debug("Profile Response:", response);

      if (response.success && response.data) {
        const profileData = response.data;

        setProfile({
          name: profileData.name || "",
          email: profileData.email || "",
          phone: profileData.phone || "",
          address: profileData.address || "",

          officeName: profileData.officeName || profileData.office_name || "",
          officeAddress:
            profileData.officeAddress || profileData.office_address || "",
          officePhone:
            profileData.officePhone || profileData.office_phone || "",

          licenseNumber:
            profileData.licenseNumber || profileData.license_number || "",
          taxNumber: profileData.taxNumber || profileData.tax_number || "",
          commercialRegister:
            profileData.commercialRegister ||
            profileData.commercial_register ||
            "",

          bankName: profileData.bankName || profileData.bank_name || "",
          bankAccount:
            profileData.bankAccount || profileData.bank_account || "",

          logo: profileData.profile_image || profileData.logo || null,

          subscription: {
            plan: profileData.subscription_plan || "مجاني",
            expiryDate: profileData.subscription_expiry
              ? new Date(profileData.subscription_expiry).toLocaleDateString(
                  "ar-EG"
                )
              : "غير محدد",

            casesLimit: profileData.cases_limit || 10,
            casesUsed: profileData.stats?.total_cases || 0,

            documentsLimit: profileData.documents_limit || 50,
            documentsUsed: profileData.stats?.total_documents || 0,

            storageLimit: profileData.storage_limit || 100,
            storageUsed: Math.round(
              (profileData.stats?.total_storage_used || 0) / (1024 * 1024)
            ),
          },

          stats: profileData.stats || {
            total_cases: 0,
            active_cases: 0,
            completed_cases: 0,
            total_clients: 0,
            total_documents: 0,
          },
        });
      } else {
        throw new Error("البيانات المستلمة غير صحيحة");
      }
    } catch (error) {
      logger.error("Load Profile Error:", error);
      message.error(error.message || "فشل في جلب بيانات الملف الشخصي");
    } finally {
      setLoading(false);
    }
  };

  // ✅ جلب بيانات الاشتراك من API منفصل
  const loadSubscriptionStatus = async () => {
    try {
      const response = await axiosInstance.get("/subscription/status");
      logger.debug("Subscription Status Response:", response.data);

      if (response.data.success && response.data.data) {
        setSubscriptionData(response.data.data);
      }
    } catch (error) {
      logger.error("Error loading subscription status:", error);
      // لا نظهر رسالة خطأ هنا لأنه قد يكون المستخدم ليس لديه اشتراك
    }
  };

  // ✅ جلب بيانات المكتب (Tenant) من API
  const loadTenantData = async () => {
    try {
      const response = await axiosInstance.get("/tenants/current");
      logger.debug("Tenant Data Response:", response.data);

      if (response.data.success && response.data.data) {
        setTenantData(response.data.data);
      }
    } catch (error) {
      logger.error("Error loading tenant data:", error);
      // لا نظهر رسالة خطأ - قد يكون المستخدم ليس في مكتب
    }
  };

  const handleLogoChange = async (file) => {
    try {
      setSaving(true);
      await profileService.uploadLogo(file);
      message.success("تم تحديث الشعار بنجاح");
      await loadProfile();
    } catch (error) {
      message.error("فشل في تحديث الشعار");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateProfile = async (values) => {
    try {
      setSaving(true);
      await profileService.updateProfile(values);
      message.success("تم تحديث الملف الشخصي بنجاح");
      await loadProfile();
      setIsEditModalVisible(false);
    } catch (error) {
      message.error("فشل في تحديث الملف الشخصي");
    } finally {
      setSaving(false);
    }
  };

  // ✅ تحديد حالة الاشتراك وألوانها
  const getSubscriptionStatusInfo = () => {
    if (!subscriptionData?.subscription) {
      return {
        status: "unknown",
        color: "gray",
        icon: <ClockCircleOutlined />,
        text: "غير محدد",
        bgGradient: "from-gray-400 to-gray-600",
      };
    }

    const sub = subscriptionData.subscription;

    if (sub.isTrial) {
      return {
        status: "trial",
        color: "orange",
        icon: <ClockCircleOutlined className="text-orange-500" />,
        text: "فترة تجريبية",
        bgGradient: "from-orange-400 to-orange-600",
      };
    } else if (sub.isActive) {
      return {
        status: "active",
        color: "green",
        icon: <CheckCircleOutlined className="text-green-500" />,
        text: "مفعل",
        bgGradient: "from-green-400 to-green-600",
      };
    } else if (sub.isExpired) {
      return {
        status: "expired",
        color: "red",
        icon: <WarningOutlined className="text-red-500" />,
        text: "منتهي",
        bgGradient: "from-red-400 to-red-600",
      };
    }

    return {
      status: "unknown",
      color: "gray",
      icon: <ClockCircleOutlined />,
      text: "غير محدد",
      bgGradient: "from-gray-400 to-gray-600",
    };
  };

  // ✅ تنسيق التاريخ
  const formatDate = (dateString) => {
    if (!dateString) return "غير محدد";
    return moment(dateString).format("DD MMMM YYYY");
  };

  const statusInfo = getSubscriptionStatusInfo();

  const pageContent = (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto" dir="rtl">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* تنبيه انتهاء الاشتراك */}
        {subscriptionData?.subscription?.showWarning && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <Alert
              message={subscriptionData.subscription.warningMessage}
              type="warning"
              showIcon
              icon={<WarningOutlined />}
              action={
                <Button
                  size="small"
                  type="primary"
                  onClick={() => navigate("/pricing")}
                >
                  ترقية الآن
                </Button>
              }
            />
          </motion.div>
        )}

        <Row gutter={[24, 24]}>
          {/* بطاقة المعلومات الشخصية */}
          <Col xs={24} lg={16}>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Card
                className="shadow-lg rounded-2xl overflow-hidden border-0"
                styles={{
                  header: {
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    borderBottom: "none",
                    padding: "16px 24px",
                  },
                  body: {
                    padding: "24px",
                  },
                }}
                title={
                  <div className="flex items-center gap-3 text-white">
                    <UserOutlined className="text-xl" />
                    <span className="text-lg font-bold">المعلومات الشخصية</span>
                  </div>
                }
                extra={
                  <Button
                    type="default"
                    icon={<EditOutlined />}
                    onClick={() => setIsEditModalVisible(true)}
                    className="bg-white/20 border-white/30 text-white hover:bg-white/30"
                  >
                    تعديل
                  </Button>
                }
              >
                <div className="flex flex-col md:flex-row items-start gap-8">
                  {/* صورة الملف الشخصي */}
                  <div className="flex flex-col items-center gap-4 mx-auto md:mx-0">
                    <div className="relative group">
                      <Avatar
                        size={140}
                        src={profile?.logo}
                        icon={!profile?.logo && <UserOutlined />}
                        className="shadow-xl border-4 border-white"
                        style={{
                          background: profile?.logo
                            ? undefined
                            : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        }}
                      />
                      <Upload
                        showUploadList={false}
                        beforeUpload={(file) => {
                          handleLogoChange(file);
                          return false;
                        }}
                      >
                        <Tooltip title="تغيير الصورة">
                          <div className="absolute bottom-2 right-2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center cursor-pointer hover:scale-110 transition-transform">
                            <CameraOutlined className="text-purple-600 text-lg" />
                          </div>
                        </Tooltip>
                      </Upload>
                    </div>
                    <div className="text-center">
                      <h3 className="text-xl font-bold text-gray-800">
                        {profile?.name || "المستخدم"}
                      </h3>
                      <p className="text-gray-500 text-sm">
                        {profile?.officeName || "مكتب المحاماة"}
                      </p>
                    </div>
                  </div>

                  {/* معلومات التفاصيل */}
                  <div className="flex-1 w-full">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <InfoItem
                        icon={<MailOutlined className="text-blue-500" />}
                        label="البريد الإلكتروني"
                        value={profile?.email}
                      />
                      <InfoItem
                        icon={<PhoneOutlined className="text-green-500" />}
                        label="رقم الهاتف"
                        value={profile?.phone}
                      />
                      <InfoItem
                        icon={<HomeOutlined className="text-orange-500" />}
                        label="العنوان"
                        value={profile?.address}
                      />
                      <InfoItem
                        icon={<BankOutlined className="text-purple-500" />}
                        label="اسم المكتب"
                        value={profile?.officeName}
                      />
                      <InfoItem
                        icon={<IdcardOutlined className="text-cyan-500" />}
                        label="رقم الترخيص"
                        value={profile?.licenseNumber}
                      />
                      <InfoItem
                        icon={<SafetyOutlined className="text-red-500" />}
                        label="السجل التجاري"
                        value={profile?.commercialRegister}
                      />
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>
          </Col>

          {/* بطاقة الاشتراك المحسنة */}
          <Col xs={24} lg={8}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="h-full"
            >
              <Card
                className="shadow-lg rounded-2xl overflow-hidden border-0 h-full"
                styles={{
                  header: {
                    background: `linear-gradient(135deg, ${
                      statusInfo.status === "trial"
                        ? "#f97316, #ea580c"
                        : statusInfo.status === "active"
                        ? "#22c55e, #16a34a"
                        : statusInfo.status === "expired"
                        ? "#ef4444, #dc2626"
                        : "#6b7280, #4b5563"
                    })`,
                    borderBottom: "none",
                    padding: "16px 24px",
                  },
                  body: {
                    padding: "24px",
                  },
                }}
                title={
                  <div className="flex items-center gap-3 text-white">
                    <CrownOutlined className="text-xl" />
                    <span className="text-lg font-bold">الاشتراك</span>
                    <Badge
                      count={statusInfo.text}
                      style={{
                        backgroundColor:
                          statusInfo.status === "trial"
                            ? "#fff7ed"
                            : statusInfo.status === "active"
                            ? "#f0fdf4"
                            : "#fef2f2",
                        color:
                          statusInfo.status === "trial"
                            ? "#ea580c"
                            : statusInfo.status === "active"
                            ? "#16a34a"
                            : "#dc2626",
                        fontWeight: "bold",
                      }}
                    />
                  </div>
                }
              >
                <div className="space-y-5">
                  {/* اسم الخطة */}
                  <div className="text-center p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <RocketOutlined className="text-purple-500 text-xl" />
                      <span className="text-lg font-bold text-gray-800">
                        {subscriptionData?.plan?.name || profile.subscription?.plan || "الخطة التجريبية"}
                      </span>
                    </div>
                    <div className="text-sm text-gray-500">
                      {subscriptionData?.tenant?.name || "مكتب المحاماة"}
                    </div>
                  </div>

                  {/* تاريخ الانتهاء */}
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-3 mb-3">
                      <CalendarOutlined className="text-blue-500 text-lg" />
                      <span className="font-medium text-gray-700">
                        {subscriptionData?.subscription?.isTrial
                          ? "تاريخ انتهاء الفترة التجريبية"
                          : "تاريخ انتهاء الاشتراك"}
                      </span>
                    </div>
                    <div className="text-2xl font-bold text-gray-800 mr-7">
                      {subscriptionData?.subscription?.isTrial
                        ? formatDate(subscriptionData?.subscription?.trialEndsAt)
                        : formatDate(subscriptionData?.subscription?.subscriptionEndsAt)}
                    </div>

                    {/* الأيام المتبقية */}
                    {subscriptionData?.subscription?.daysRemaining && (
                      <div
                        className={`mt-3 mr-7 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
                          subscriptionData.subscription.daysRemaining <= 3
                            ? "bg-red-100 text-red-700"
                            : subscriptionData.subscription.daysRemaining <= 7
                            ? "bg-orange-100 text-orange-700"
                            : "bg-green-100 text-green-700"
                        }`}
                      >
                        <ClockCircleOutlined />
                        <span>
                          {subscriptionData.subscription.daysRemaining} يوم متبقي
                        </span>
                      </div>
                    )}
                  </div>

                  {/* إحصائيات الاستخدام */}
                  <div className="space-y-4">
                    <UsageProgress
                      icon={<FolderOutlined className="text-blue-500" />}
                      label="القضايا"
                      used={profile.subscription?.casesUsed || 0}
                      limit={profile.subscription?.casesLimit || 10}
                      color="#3b82f6"
                    />
                    <UsageProgress
                      icon={<FileTextOutlined className="text-green-500" />}
                      label="المستندات"
                      used={profile.subscription?.documentsUsed || 0}
                      limit={profile.subscription?.documentsLimit || 50}
                      color="#22c55e"
                    />
                    <UsageProgress
                      icon={<CloudOutlined className="text-purple-500" />}
                      label="المساحة (MB)"
                      used={profile.subscription?.storageUsed || 0}
                      limit={profile.subscription?.storageLimit || 100}
                      color="#8b5cf6"
                    />
                  </div>

                  {/* زر الترقية */}
                  <Button
                    type="primary"
                    block
                    size="large"
                    icon={<RocketOutlined />}
                    onClick={() => navigate("/pricing")}
                    className="mt-4 h-12 text-base font-medium rounded-xl"
                    style={{
                      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      border: "none",
                    }}
                  >
                    {subscriptionData?.subscription?.isTrial
                      ? "اشترك الآن"
                      : "ترقية الاشتراك"}
                  </Button>
                </div>
              </Card>
            </motion.div>
          </Col>

          {/* ✅ بطاقة معلومات المكتب */}
          {tenantData && (
            <Col xs={24}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
              >
                <Card
                  className="shadow-lg rounded-2xl overflow-hidden border-0"
                  styles={{
                    header: {
                      background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                      borderBottom: "none",
                      padding: "16px 24px",
                    },
                    body: {
                      padding: "24px",
                    },
                  }}
                  title={
                    <div className="flex items-center gap-3 text-white">
                      <BankOutlined className="text-xl" />
                      <span className="text-lg font-bold">
                        معلومات المكتب
                      </span>
                      <Badge
                        count={tenantData.status === "trial" ? "تجريبي" : tenantData.status === "active" ? "نشط" : tenantData.status}
                        style={{
                          backgroundColor: tenantData.status === "trial" ? "#fff7ed" : "#f0fdf4",
                          color: tenantData.status === "trial" ? "#ea580c" : "#16a34a",
                          fontWeight: "bold",
                        }}
                      />
                    </div>
                  }
                >
                  <Row gutter={[24, 16]}>
                    <Col xs={24} md={8}>
                      <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl h-full">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                            <BankOutlined className="text-blue-600 text-xl" />
                          </div>
                          <div>
                            <div className="text-xs text-gray-500">اسم المكتب</div>
                            <div className="text-lg font-bold text-gray-800">
                              {tenantData.name || "غير محدد"}
                            </div>
                          </div>
                        </div>
                        <div className="text-sm text-gray-500 mt-2">
                          <span className="font-medium">النطاق: </span>
                          <code className="bg-white px-2 py-1 rounded text-blue-600">
                            {tenantData.subdomain}
                          </code>
                        </div>
                      </div>
                    </Col>
                    <Col xs={24} md={8}>
                      <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl h-full">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                            <MailOutlined className="text-green-600 text-xl" />
                          </div>
                          <div>
                            <div className="text-xs text-gray-500">بريد التواصل</div>
                            <div className="text-base font-medium text-gray-800">
                              {tenantData.contact_email || "غير محدد"}
                            </div>
                          </div>
                        </div>
                        {tenantData.contact_phone && (
                          <div className="flex items-center gap-2 text-sm text-gray-600 mt-2">
                            <PhoneOutlined />
                            <span>{tenantData.contact_phone}</span>
                          </div>
                        )}
                      </div>
                    </Col>
                    <Col xs={24} md={8}>
                      <div className="p-4 bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl h-full">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                            <CalendarOutlined className="text-orange-600 text-xl" />
                          </div>
                          <div>
                            <div className="text-xs text-gray-500">
                              {tenantData.status === "trial" ? "انتهاء الفترة التجريبية" : "انتهاء الاشتراك"}
                            </div>
                            <div className="text-lg font-bold text-gray-800">
                              {tenantData.status === "trial" 
                                ? formatDate(tenantData.trial_ends_at)
                                : formatDate(tenantData.subscription_ends_at)}
                            </div>
                          </div>
                        </div>
                        {tenantData.daysRemaining && (
                          <div className={`text-sm font-medium ${
                            tenantData.daysRemaining <= 3 ? "text-red-600" : 
                            tenantData.daysRemaining <= 7 ? "text-orange-600" : "text-green-600"
                          }`}>
                            <ClockCircleOutlined className="mr-1" />
                            {tenantData.daysRemaining} يوم متبقي
                          </div>
                        )}
                      </div>
                    </Col>
                  </Row>

                  {/* إحصائيات المكتب */}
                  {(tenantData.total_clients > 0 || tenantData.total_cases > 0) && (
                    <div className="mt-6 pt-4 border-t border-gray-100">
                      <Row gutter={16}>
                        <Col xs={8}>
                          <div className="text-center">
                            <div className="text-2xl font-bold text-blue-600">
                              {tenantData.total_users || 0}
                            </div>
                            <div className="text-xs text-gray-500">المستخدمين</div>
                          </div>
                        </Col>
                        <Col xs={8}>
                          <div className="text-center">
                            <div className="text-2xl font-bold text-green-600">
                              {tenantData.total_clients || 0}
                            </div>
                            <div className="text-xs text-gray-500">العملاء</div>
                          </div>
                        </Col>
                        <Col xs={8}>
                          <div className="text-center">
                            <div className="text-2xl font-bold text-purple-600">
                              {tenantData.total_cases || 0}
                            </div>
                            <div className="text-xs text-gray-500">القضايا</div>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  )}
                </Card>
              </motion.div>
            </Col>
          )}

          {/* بطاقة المعلومات المالية */}
          <Col xs={24}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
            >
              <Card
                className="shadow-lg rounded-2xl overflow-hidden border-0"
                styles={{
                  header: {
                    background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                    borderBottom: "none",
                    padding: "16px 24px",
                  },
                  body: {
                    padding: "24px",
                  },
                }}
                title={
                  <div className="flex items-center gap-3 text-white">
                    <DollarOutlined className="text-xl" />
                    <span className="text-lg font-bold">
                      المعلومات المالية والقانونية
                    </span>
                  </div>
                }
              >
                <Row gutter={[24, 24]}>
                  <Col xs={24} md={12}>
                    <div className="p-4 bg-gray-50 rounded-xl space-y-4">
                      <h4 className="font-bold text-gray-700 mb-4 flex items-center gap-2">
                        <IdcardOutlined className="text-blue-500" />
                        البيانات القانونية
                      </h4>
                      <InfoItem
                        icon={<FileTextOutlined className="text-gray-500" />}
                        label="السجل التجاري"
                        value={profile?.commercialRegister}
                        compact
                      />
                      <InfoItem
                        icon={<SafetyOutlined className="text-gray-500" />}
                        label="الرقم الضريبي"
                        value={profile?.taxNumber}
                        compact
                      />
                    </div>
                  </Col>
                  <Col xs={24} md={12}>
                    <div className="p-4 bg-gray-50 rounded-xl space-y-4">
                      <h4 className="font-bold text-gray-700 mb-4 flex items-center gap-2">
                        <BankOutlined className="text-green-500" />
                        البيانات البنكية
                      </h4>
                      <InfoItem
                        icon={<BankOutlined className="text-gray-500" />}
                        label="اسم البنك"
                        value={profile?.bankName}
                        compact
                      />
                      <InfoItem
                        icon={<IdcardOutlined className="text-gray-500" />}
                        label="رقم الحساب (IBAN)"
                        value={profile?.bankAccount}
                        compact
                      />
                    </div>
                  </Col>
                </Row>
              </Card>
            </motion.div>
          </Col>
        </Row>
      </motion.div>

      {/* نافذة تعديل المعلومات */}
      <Modal
        title={
          <div className="text-xl flex items-center gap-2">
            <EditOutlined className="text-purple-500" />
            تعديل المعلومات الشخصية
          </div>
        }
        open={isEditModalVisible}
        onCancel={() => setIsEditModalVisible(false)}
        footer={null}
        width={800}
        className="rtl"
        style={{ direction: "rtl" }}
      >
        <Form
          layout="vertical"
          initialValues={profile}
          onFinish={handleUpdateProfile}
          className="mt-4"
          dir="rtl"
        >
          <Row gutter={16}>
            <Col span={24}>
              <div className="bg-blue-50 p-4 rounded-lg mb-6">
                <h3 className="text-lg font-semibold mb-4 text-blue-900 flex items-center gap-2">
                  <UserOutlined />
                  المعلومات الشخصية
                </h3>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="name"
                      label="الاسم"
                      rules={[
                        { required: true, message: "الرجاء إدخال الاسم" },
                      ]}
                    >
                      <Input placeholder="أدخل الاسم الكامل" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="email"
                      label="البريد الإلكتروني"
                      rules={[
                        {
                          required: true,
                          message: "الرجاء إدخال البريد الإلكتروني",
                        },
                        {
                          type: "email",
                          message: "صيغة البريد الإلكتروني غير صحيحة",
                        },
                      ]}
                    >
                      <Input placeholder="example@domain.com" />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="phone" label="رقم الهاتف">
                      <Input placeholder="05xxxxxxxx" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="address" label="العنوان">
                      <Input placeholder="المدينة، الحي، الشارع" />
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              <div className="bg-green-50 p-4 rounded-lg mb-6">
                <h3 className="text-lg font-semibold mb-4 text-green-900 flex items-center gap-2">
                  <BankOutlined />
                  معلومات المكتب
                </h3>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="officeName" label="اسم المكتب">
                      <Input placeholder="أدخل اسم المكتب" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="officePhone" label="هاتف المكتب">
                      <Input placeholder="رقم هاتف المكتب" />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="licenseNumber" label="رقم الترخيص">
                      <Input placeholder="رقم ترخيص المحاماة" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="commercialRegister" label="السجل التجاري">
                      <Input placeholder="رقم السجل التجاري" />
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold mb-4 text-purple-900 flex items-center gap-2">
                  <DollarOutlined />
                  المعلومات البنكية
                </h3>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="bankName" label="اسم البنك">
                      <Input placeholder="اسم البنك" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="bankAccount" label="رقم الحساب (IBAN)">
                      <Input placeholder="SA0000000000000000000000" />
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>

          <div className="flex justify-start gap-2 mt-6 border-t pt-4">
            <Button
              type="primary"
              htmlType="submit"
              loading={saving}
              size="large"
              style={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                border: "none",
              }}
            >
              حفظ التغييرات
            </Button>
            <Button onClick={() => setIsEditModalVisible(false)} size="large">
              إلغاء
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );

  return (
    <Layout>
      {loading ? (
        <div className="flex justify-center items-center min-h-screen">
          <Spin size="large" tip="جاري التحميل..." />
        </div>
      ) : (
        pageContent
      )}
    </Layout>
  );
};

// ✅ مكون عرض المعلومة
const InfoItem = ({ icon, label, value, compact = false }) => (
  <div
    className={`flex items-start gap-3 ${
      compact ? "py-2" : "p-3 bg-gray-50 rounded-lg"
    }`}
  >
    <div className="mt-0.5">{icon}</div>
    <div className="flex-1">
      <div className="text-xs text-gray-500 mb-0.5">{label}</div>
      <div className="text-sm font-medium text-gray-800">
        {value || "غير محدد"}
      </div>
    </div>
  </div>
);

// ✅ مكون شريط الاستخدام
const UsageProgress = ({ icon, label, used, limit, color }) => {
  const percent = Math.min((used / limit) * 100, 100);

  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <div className="flex items-center gap-2">
          {icon}
          <span className="font-medium text-gray-700">{label}</span>
        </div>
        <span className="text-sm text-gray-500">
          {used} / {limit}
        </span>
      </div>
      <Progress
        percent={percent}
        showInfo={false}
        strokeColor={color}
        trailColor="#e5e7eb"
        size="small"
      />
    </div>
  );
};

export default Profile;
